import React, { useState, useEffect, useRef, useCallback } from 'react';
import { formatTenure, formatLiveTenure, liveTenureYears } from './utils/tenure';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';
import NotesActivity from './NotesActivity';
import { parseSearchTerms } from './utils/searchTerms';

// ─── Entity Cache ─────────────────────────────────────────────────────
// Module-level cache so reopening the same entity panel is instant.
// Data is served from cache immediately, then refreshed in the background.
const entityCache = new Map<string, { data: any; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes — after this, show loading instead of stale cache

function getCacheKey(entityType: string, entityId: number, scoped?: boolean): string {
  return `${entityType}:${entityId}${scoped ? ':scoped' : ''}`;
}

function getCachedEntity(entityType: string, entityId: number, scoped?: boolean): any | null {
  const key = getCacheKey(entityType, entityId, scoped);
  const entry = entityCache.get(key);
  if (!entry) return null;
  return entry;
}

function setCachedEntity(entityType: string, entityId: number, data: any, scoped?: boolean): void {
  const key = getCacheKey(entityType, entityId, scoped);
  entityCache.set(key, { data, timestamp: Date.now() });
}

// Warm the cache before the user clicks (e.g. on row hover) so the panel
// opens instantly. Best-effort and deduped; uses the unscoped key, which
// is what candidate/contact panels read.
const inflightPrefetch = new Set<string>();
export async function prefetchEntity(entityType: string, entityId: number, token: string): Promise<void> {
  const key = getCacheKey(entityType, entityId, false);
  const entry = entityCache.get(key);
  if (entry && Date.now() - entry.timestamp < CACHE_TTL) return;
  if (inflightPrefetch.has(key)) return;
  inflightPrefetch.add(key);
  try {
    const typeMap: Record<string, string> = { candidate: 'candidates', company: 'companies', contact: 'contacts' };
    const resp = await fetch(`/api/global/${typeMap[entityType]}/${entityId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (resp.ok) entityCache.set(key, { data: await resp.json(), timestamp: Date.now() });
  } catch { /* best-effort */ } finally {
    inflightPrefetch.delete(key);
  }
}

// ─── Text Highlighting ────────────────────────────────────────────────

function highlightText(text: string | null | undefined, terms: string[]): React.ReactNode {
  if (!text || terms.length === 0) return text || null;

  // Build regex from all terms, escaped and sorted longest-first to avoid partial overlaps
  const sortedTerms = [...terms].sort((a, b) => b.length - a.length);
  const escaped = sortedTerms.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const alternation = escaped.join('|');

  // Strict "not-inside-another-word" matching (LinkedIn-style): only
  // highlight standalone occurrences, never inside another word ("wind"
  // doesn't highlight inside "winding"). Doing this with \b would silently
  // fail for terms that themselves start/end with a non-word char (e.g.
  // ".NET", "C#") because \b only fires at word↔non-word transitions.
  // Lookbehind (?<!\w) would work but isn't supported on Safari < 16.4,
  // so we use a manual scan instead: find all substring matches, then
  // accept only those whose neighbouring characters aren't word chars.
  const findRegex = new RegExp(alternation, 'gi');
  const isWordChar = (ch: string | undefined) => !!ch && /\w/.test(ch);

  const ranges: { start: number; end: number }[] = [];
  let m: RegExpExecArray | null;
  while ((m = findRegex.exec(text)) !== null) {
    const start = m.index;
    const end = start + m[0].length;
    const before = start > 0 ? text[start - 1] : undefined;
    const after = end < text.length ? text[end] : undefined;
    if (!isWordChar(before) && !isWordChar(after)) {
      ranges.push({ start, end });
    }
    if (m[0].length === 0) findRegex.lastIndex++; // safety against zero-length matches
  }

  if (ranges.length === 0) return text;

  const parts: Array<{ text: string; isMatch: boolean }> = [];
  let cursor = 0;
  for (const r of ranges) {
    if (r.start > cursor) parts.push({ text: text.slice(cursor, r.start), isMatch: false });
    parts.push({ text: text.slice(r.start, r.end), isMatch: true });
    cursor = r.end;
  }
  if (cursor < text.length) parts.push({ text: text.slice(cursor), isMatch: false });

  return (
    <>
      {parts.map((part, i) =>
        part.isMatch
          ? <mark key={i} className="bg-yellow-200 rounded-sm px-0.5">{part.text}</mark>
          : <React.Fragment key={i}>{part.text}</React.Fragment>
      )}
    </>
  );
}

// ─── Types ────────────────────────────────────────────────────────────

type EntityType = 'candidate' | 'company' | 'contact';

interface CrossProjectCopy {
  id: number;
  project_id: number;
  project_name: string;
  [key: string]: any;
}

interface BdCampaignContext {
  campaignId: number;
  targetStageId: number;
  existingContactIds: Set<number>;
  existingCandidateIds: Set<number>;
  onAddContactToCampaign: (contactId: number) => Promise<void>;
  onAddCandidateToCampaign: (candidateId: number) => Promise<void>;
  onContactCreated: (contact: any) => void;
}

interface EntityDetailPanelProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: EntityType;
  entityId: number | null;
  onEnrichmentComplete?: () => void;
  searchTerm?: string;
  filterTerms?: string[];
  onNavigateNext?: () => void;
  onNavigatePrev?: () => void;
  bdCampaignContext?: BdCampaignContext;
  projectId?: number; // When set, scopes candidates/HMs to this project only
}

// ─── Component ────────────────────────────────────────────────────────

const EntityDetailPanel: React.FC<EntityDetailPanelProps> = ({
  isOpen,
  onClose,
  entityType,
  entityId,
  onEnrichmentComplete,
  searchTerm,
  filterTerms,
  onNavigateNext,
  onNavigatePrev,
  bdCampaignContext,
  projectId,
}) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [entity, setEntity] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [enriching, setEnriching] = useState<string | null>(null);
  const [enrichError, setEnrichError] = useState<string | null>(null);

  // Sub-entity panel (stacked on top for clicking linked candidates/contacts)
  const [subEntity, setSubEntity] = useState<{ type: EntityType; id: number } | null>(null);
  const [panelTab, setPanelTab] = useState<'details' | 'notes'>('details');
  const contentRef = useRef<HTMLDivElement>(null);

  const isProjectScoped = entityType === 'company' && !!(projectId || bdCampaignContext);

  useEffect(() => {
    if (isOpen && entityId) {
      // Serve cached data instantly if available
      const cached = getCachedEntity(entityType, entityId, isProjectScoped);
      if (cached) {
        setEntity(cached.data);
        // If cache is fresh enough, just do a background refresh without spinner
        if (Date.now() - cached.timestamp < CACHE_TTL) {
          setLoading(false);
          // Background refresh to keep data fresh
          fetchEntityBackground();
        } else {
          // Stale cache — show data but also show we're refreshing
          fetchEntity();
        }
      } else {
        fetchEntity();
      }
      setSubEntity(null);
      setPanelTab('details');
    } else {
      setEntity(null);
      setSubEntity(null);
      setPanelTab('details');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, entityId, entityType]);

  // Arrow key navigation between candidates
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't intercept if user is typing in an input/textarea
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      if (e.key === 'ArrowRight' && onNavigateNext) {
        e.preventDefault();
        e.stopPropagation();
        onNavigateNext();
      } else if (e.key === 'ArrowLeft' && onNavigatePrev) {
        e.preventDefault();
        e.stopPropagation();
        onNavigatePrev();
      } else if (e.key === 'ArrowDown' && contentRef.current) {
        e.preventDefault();
        contentRef.current.scrollBy({ top: 120, behavior: 'smooth' });
      } else if (e.key === 'ArrowUp' && contentRef.current) {
        e.preventDefault();
        contentRef.current.scrollBy({ top: -120, behavior: 'smooth' });
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, onNavigateNext, onNavigatePrev]);

  // Build the fetch URL, adding projectScoped=true for companies in BD campaign context
  const buildEntityUrl = useCallback((type: EntityType, id: number) => {
    const typeMap = { candidate: 'candidates', company: 'companies', contact: 'contacts' };
    let url = `/api/global/${typeMap[type]}/${id}`;
    if (type === 'company' && (projectId || bdCampaignContext)) {
      url += '?projectScoped=true';
    }
    return url;
  }, [projectId, bdCampaignContext]);

  // Background refresh — updates data silently without showing a loading spinner
  const fetchEntityBackground = useCallback(async () => {
    if (!entityId || !token) return;
    try {
      const resp = await fetch(buildEntityUrl(entityType, entityId), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Failed to fetch');
      const data = await resp.json();
      setCachedEntity(entityType, entityId, data, isProjectScoped);
      setEntity(data);
    } catch (err) {
      // Silent failure for background refresh — we already have cached data
      console.error('Background refresh failed', err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, entityType, token, buildEntityUrl, isProjectScoped]);

  const fetchEntity = async () => {
    if (!entityId || !token) return;
    setLoading(true);
    try {
      const resp = await fetch(buildEntityUrl(entityType, entityId), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Failed to fetch');
      const data = await resp.json();
      setCachedEntity(entityType, entityId, data, isProjectScoped);
      setEntity(data);
    } catch (err) {
      console.error('Failed to load entity', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEnrich = async (enrichType: string) => {
    if (!entity || !token) return;
    setEnriching(enrichType);
    setEnrichError(null);

    try {
      const entityPath = entityType === 'candidate' ? 'candidate' : 'manager';
      const resp = await fetch(`/api/fullenrich/enrich/${entityPath}/${entity.id}?type=${enrichType}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });

      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        throw new Error(errData.error || 'Enrichment failed');
      }

      const result = await resp.json();

      if (result.instant) {
        await fetchEntity();
        onEnrichmentComplete?.();
        // Apollo: phone numbers arrive ~3-5s later via webhook, do a silent re-fetch
        if (result.provider === 'apollo') {
          // Keep spinner running until the webhook follow-up lands so the
          // user can see it's still working.
          setTimeout(async () => {
            await fetchEntity();
            onEnrichmentComplete?.();
            setEnriching(null);
          }, 6000);
        } else {
          setEnriching(null);
        }
      } else {
        // FullEnrich is async — server returns "queued" immediately, the
        // actual contact arrives via webhook a few seconds later. Keep the
        // spinner spinning until the data has come back so the user knows
        // something's still happening.
        setTimeout(async () => {
          await fetchEntity();
          onEnrichmentComplete?.();
          setEnriching(null);
        }, 5000);
      }
    } catch (err: any) {
      setEnrichError(err.message || 'Enrichment failed');
      setEnriching(null);
    }
  };

  const handleLinkedEntityClick = (type: EntityType, id: number) => {
    setSubEntity({ type, id });
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="modal-backdrop"
        onClick={onClose}
      />

      {/* Main Panel */}
      <div className="slide-panel w-full max-w-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200/80 bg-gray-50/50">
          <div className="flex items-center gap-3">
            <EntityIcon type={entityType} />
            <div>
              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                {entityType === 'contact' ? 'Hiring Manager' : entityType}
              </span>
              {entity && (
                <h2 className="text-lg font-semibold text-gray-900 leading-tight tracking-tight">
                  {entity.name || entity.company_name || 'Loading...'}
                </h2>
              )}
            </div>
          </div>
          <div className="flex items-center gap-0.5">
            {onNavigatePrev && (
              <button data-tour="profile-prev" onClick={onNavigatePrev} className="p-2 hover:bg-gray-100 rounded-lg transition-all duration-200" title="Previous (←)">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            {onNavigateNext && (
              <button data-tour="profile-next" onClick={onNavigateNext} className="p-2 hover:bg-gray-100 rounded-lg transition-all duration-200" title="Next (→)">
                <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            )}
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-all duration-200 ml-1">
              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Tab bar */}
        {entity && !loading && (
          <div className="flex border-b border-gray-200 px-6">
            <button
              onClick={() => setPanelTab('details')}
              className={`px-4 py-2.5 text-sm font-medium transition-colors -mb-px ${
                panelTab === 'details'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Details
            </button>
            <button
              onClick={() => setPanelTab('notes')}
              className={`px-4 py-2.5 text-sm font-medium transition-colors -mb-px ${
                panelTab === 'notes'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Notes & Activity
            </button>
          </div>
        )}

        {/* Content */}
        <div ref={contentRef} className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : entity ? (
            panelTab === 'details' ? (
              <div className="divide-y divide-gray-100">
                {entityType === 'candidate' && <CandidateDetail entity={entity} onEnrich={handleEnrich} enriching={enriching} enrichError={enrichError} navigate={navigate} searchTerm={searchTerm} filterTerms={filterTerms} onTagChange={() => { fetchEntity(); onEnrichmentComplete?.(); }} projectId={projectId} onRefresh={fetchEntity} />}
                {entityType === 'company' && <CompanyDetail entity={entity} navigate={navigate} onEntityClick={handleLinkedEntityClick} bdCampaignContext={bdCampaignContext} onRefresh={fetchEntity} />}
                {entityType === 'contact' && <ContactDetail entity={entity} onEnrich={handleEnrich} enriching={enriching} enrichError={enrichError} navigate={navigate} />}

                {/* Cross-project presence */}
                {entity.cross_project_copies && entity.cross_project_copies.length > 0 && (
                  <Section title="Also appears in">
                    <div className="space-y-2">
                      {entity.cross_project_copies.map((copy: CrossProjectCopy) => (
                        <button
                          key={copy.id}
                          onClick={() => navigate(`/project/${copy.project_id}`)}
                          className="flex items-center gap-2 w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                        >
                          <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                          </svg>
                          <span className="text-gray-700">{copy.project_name}</span>
                          {copy.enrichment_status === 'found' && (
                            <span className="ml-auto text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">enriched</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </Section>
                )}
              </div>
            ) : (
              <NotesActivity entityType={entityType} entityId={entity.id} />
            )
          ) : (
            <div className="flex items-center justify-center py-20 text-gray-400">
              No data available
            </div>
          )}
        </div>
      </div>

      {/* Stacked sub-entity panel (overlays on top) */}
      {subEntity && (
        <SubEntityPanel
          entityType={subEntity.type}
          entityId={subEntity.id}
          onClose={() => setSubEntity(null)}
          onEnrichmentComplete={onEnrichmentComplete}
        />
      )}
    </>
  );
};


// ─── Sub-Entity Panel (stacked overlay) ───────────────────────────────

const SubEntityPanel: React.FC<{
  entityType: EntityType;
  entityId: number;
  onClose: () => void;
  onEnrichmentComplete?: () => void;
}> = ({ entityType, entityId, onClose, onEnrichmentComplete }) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [entity, setEntity] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [enriching, setEnriching] = useState<string | null>(null);
  const [enrichError, setEnrichError] = useState<string | null>(null);

  useEffect(() => {
    // Serve cached data instantly if available
    const cached = getCachedEntity(entityType, entityId);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      setEntity(cached.data);
      setLoading(false);
      // Background refresh
      fetchEntitySilent();
    } else {
      if (cached) setEntity(cached.data);
      fetchEntity();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId, entityType]);

  const fetchEntitySilent = async () => {
    if (!token) return;
    try {
      const typeMap = { candidate: 'candidates', company: 'companies', contact: 'contacts' };
      const resp = await fetch(`/api/global/${typeMap[entityType]}/${entityId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Failed to fetch');
      const data = await resp.json();
      setCachedEntity(entityType, entityId, data);
      setEntity(data);
    } catch (err) {
      console.error('Background refresh failed for sub-entity', err);
    }
  };

  const fetchEntity = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const typeMap = { candidate: 'candidates', company: 'companies', contact: 'contacts' };
      const resp = await fetch(`/api/global/${typeMap[entityType]}/${entityId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Failed to fetch');
      const data = await resp.json();
      setCachedEntity(entityType, entityId, data);
      setEntity(data);
    } catch (err) {
      console.error('Failed to load sub-entity', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEnrich = async (enrichType: string) => {
    if (!entity || !token) return;
    setEnriching(enrichType);
    setEnrichError(null);
    try {
      const entityPath = entityType === 'candidate' ? 'candidate' : 'manager';
      const resp = await fetch(`/api/fullenrich/enrich/${entityPath}/${entity.id}?type=${enrichType}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!resp.ok) {
        const errData = await resp.json().catch(() => ({}));
        throw new Error(errData.error || 'Enrichment failed');
      }
      const result = await resp.json();
      if (result.instant) {
        await fetchEntity();
        onEnrichmentComplete?.();
        if (result.provider === 'apollo') {
          // Keep spinner running until Apollo's webhook follow-up lands.
          setTimeout(async () => {
            await fetchEntity();
            onEnrichmentComplete?.();
            setEnriching(null);
          }, 6000);
        } else {
          setEnriching(null);
        }
      } else {
        // FullEnrich is async — wait for the webhook to arrive before
        // clearing the spinner so the user can see it's still working.
        setTimeout(async () => {
          await fetchEntity();
          onEnrichmentComplete?.();
          setEnriching(null);
        }, 5000);
      }
    } catch (err: any) {
      setEnrichError(err.message || 'Enrichment failed');
      setEnriching(null);
    }
  };

  return (
    <>
      {/* Darker overlay on top of existing panel */}
      <div
        className="fixed inset-0 bg-black/20 z-[55] transition-opacity"
        onClick={onClose}
      />

      {/* Stacked panel — slightly narrower, higher z-index */}
      <div className="fixed top-0 right-0 h-full w-full max-w-lg bg-white shadow-2xl z-[60] flex flex-col overflow-hidden animate-slide-in-right">
        {/* Header with back button */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors mr-1"
              title="Back to company"
            >
              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <EntityIcon type={entityType} />
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                {entityType === 'contact' ? 'Hiring Manager' : entityType}
              </span>
              {entity && (
                <h2 className="text-lg font-semibold text-gray-900 leading-tight">
                  {entity.name || 'Loading...'}
                </h2>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : entity ? (
            <div className="divide-y divide-gray-100">
              {entityType === 'candidate' && <CandidateDetail entity={entity} onEnrich={handleEnrich} enriching={enriching} enrichError={enrichError} navigate={navigate} onTagChange={() => { fetchEntity(); onEnrichmentComplete?.(); }} onRefresh={fetchEntity} />}
              {entityType === 'contact' && <ContactDetail entity={entity} onEnrich={handleEnrich} enriching={enriching} enrichError={enrichError} navigate={navigate} />}

              {/* Cross-project presence */}
              {entity.cross_project_copies && entity.cross_project_copies.length > 0 && (
                <Section title="Also appears in">
                  <div className="space-y-2">
                    {entity.cross_project_copies.map((copy: CrossProjectCopy) => (
                      <button
                        key={copy.id}
                        onClick={() => navigate(`/project/${copy.project_id}`)}
                        className="flex items-center gap-2 w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                      >
                        <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                        </svg>
                        <span className="text-gray-700">{copy.project_name}</span>
                        {copy.enrichment_status === 'found' && (
                          <span className="ml-auto text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">enriched</span>
                        )}
                      </button>
                    ))}
                  </div>
                </Section>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center py-20 text-gray-400">
              No data available
            </div>
          )}
        </div>
      </div>
    </>
  );
};


// ─── Candidate Detail ─────────────────────────────────────────────────


// Monitoring change history for a candidate. Shows EVERY recorded change
// (job moves, headline/summary/skills edits) — previously only high-priority
// changes surfaced anywhere, so e.g. a promotion-flavoured headline change
// was stored but invisible.
const ChangeHistorySection: React.FC<{ candidateId: number; token: string | null }> = ({ candidateId, token }) => {
  const [changes, setChanges] = useState<any[]>([]);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/monitoring/candidates/${candidateId}/changes`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (!cancelled && d) setChanges(d.changes || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [candidateId, token]);

  if (changes.length === 0) return null;

  const parseVal = (v: any): string => {
    if (v == null) return '';
    let val = v;
    if (typeof val === 'string') {
      try { val = JSON.parse(val); } catch { return val; }
    }
    if (typeof val === 'object') {
      const parts = [val.title, val.company].filter(Boolean);
      return parts.length ? parts.join(' at ') : JSON.stringify(val);
    }
    return String(val);
  };

  const typeLabel = (t: string) => (t || 'change').replace(/_/g, ' ');
  const catStyle = (c: string) =>
    c === 'high_priority' ? 'bg-red-50 text-red-700 border-red-200'
    : c === 'medium_priority' ? 'bg-amber-50 text-amber-700 border-amber-200'
    : 'bg-gray-50 text-gray-500 border-gray-200';

  return (
    <Section title={`Change History (${changes.length})`}>
      <div className="space-y-3">
        {changes.slice(0, 10).map(ch => (
          <div key={ch.id} className="text-sm border-l-2 border-gray-200 pl-3">
            <div className="flex items-center gap-2">
              <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded border capitalize ${catStyle(ch.change_category)}`}>
                {typeLabel(ch.change_type)}
              </span>
              <span className="text-xs text-gray-400">
                {new Date(ch.detected_at).toLocaleDateString('en-GB')}
              </span>
            </div>
            {(ch.old_value || ch.new_value) && (
              <p className="text-xs text-gray-600 mt-1">
                {parseVal(ch.old_value) && <span className="line-through text-gray-400">{parseVal(ch.old_value)}</span>}
                {parseVal(ch.old_value) && parseVal(ch.new_value) && <span className="mx-1.5 text-gray-400">→</span>}
                {parseVal(ch.new_value) && <span className="text-gray-800">{parseVal(ch.new_value)}</span>}
              </p>
            )}
          </div>
        ))}
        {changes.length > 10 && (
          <p className="text-xs text-gray-400">…and {changes.length - 10} earlier changes.</p>
        )}
      </div>
    </Section>
  );
};

const CandidateDetail: React.FC<{
  entity: any;
  onEnrich: (type: string) => void;
  enriching: string | null;
  enrichError: string | null;
  navigate: any;
  searchTerm?: string;
  filterTerms?: string[];
  onTagChange?: () => void;
  projectId?: number;
  onRefresh?: () => void;
}> = ({ entity, onEnrich, enriching, enrichError, navigate, searchTerm, filterTerms, onTagChange, projectId, onRefresh }) => {
  const { token } = useAuth();
  const [translated, setTranslated] = useState<Record<string, string> | null>(null);
  const [translating, setTranslating] = useState(false);
  const [detectedLang, setDetectedLang] = useState<string | null>(null);
  const [translatedEntityId, setTranslatedEntityId] = useState<number | null>(null);

  // Reset translation when entity changes
  useEffect(() => {
    if (entity?.id !== translatedEntityId) {
      setTranslated(null);
      setDetectedLang(null);
    }
  }, [entity?.id, translatedEntityId]);

  const handleTranslate = async () => {
    if (!entity || !token) return;
    setTranslating(true);
    try {
      const fields: Record<string, string> = {};
      if (entity.headline) fields.headline = entity.headline;
      if (entity.summary) fields.summary = entity.summary;
      if (entity.current_position) fields.current_position = entity.current_position;
      if (entity.current_position_description) fields.current_position_description = entity.current_position_description;
      for (let i = 1; i <= 3; i++) {
        if (entity[`previous_position_${i}`]) fields[`previous_position_${i}`] = entity[`previous_position_${i}`];
        if (entity[`previous_position_${i}_description`]) fields[`previous_position_${i}_description`] = entity[`previous_position_${i}_description`];
      }
      // Also the full experience timeline (position_history) — titles + role
      // descriptions — keyed by index so the timeline can render translated.
      const posHistory = parsePositionHistory(entity);
      if (Array.isArray(posHistory)) {
        posHistory.forEach((pos: any, i: number) => {
          if (pos?.title) fields[`ph_${i}_title`] = pos.title;
          if (pos?.description) fields[`ph_${i}_description`] = pos.description;
        });
      }

      const resp = await fetch('/api/ai-search/translate', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ fields })
      });
      if (!resp.ok) throw new Error('Translation failed');
      const data = await resp.json();
      setTranslated(data.translated);
      setDetectedLang(data.detectedLanguage);
      setTranslatedEntityId(entity.id);
    } catch (err) {
      console.error('Translation error:', err);
    } finally {
      setTranslating(false);
    }
  };

  // Helper to get translated or original value
  const t = (field: string) => (translated && translated[field]) ? translated[field] : entity[field];

  // Combine search + filter terms for highlighting.
  // Search input is split into individual keywords (commas / newlines / OR)
  // so multi-keyword searches highlight each keyword in the side panel too.
  const allTerms = React.useMemo(() => {
    const terms: string[] = [];
    terms.push(...parseSearchTerms(searchTerm));
    if (filterTerms) terms.push(...filterTerms.filter(t => t.trim()));
    return terms;
  }, [searchTerm, filterTerms]);

  // Shorthand: highlight text with all active terms
  const hl = (text: string | null | undefined) => highlightText(text, allTerms);

  return (
    <>
      {/* Profile header */}
      <div className="px-6 py-5">
        <div className="flex items-start gap-4">
          {entity.photo_url ? (
            <img src={entity.photo_url} alt="" className="w-14 h-14 rounded-full object-cover border-2 border-gray-200" />
          ) : (
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-lg font-semibold">
              {(entity.first_name?.[0] || entity.name?.[0] || '?').toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 truncate">{hl(entity.name)}</h3>
            {entity.headline && <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">{hl(t('headline'))}</p>}
            {entity.current_position && entity.current_company && (
              <p className="text-sm text-gray-500 mt-1">
                {hl(t('current_position'))} at <span className="font-medium text-gray-700">{hl(entity.current_company)}</span>
              </p>
            )}
            {entity.location && (
              <p className="text-sm text-gray-500 mt-1 flex items-center gap-1">
                <svg className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="truncate">{hl(entity.location)}</span>
              </p>
            )}
          </div>
        </div>

        {/* Translate button */}
        <div className="mt-3 flex items-center gap-3">
          <EnrichmentBadge status={entity.enrichment_status} />
          {!translated ? (
            <button
              onClick={handleTranslate}
              disabled={translating}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-700 bg-indigo-50 rounded-full hover:bg-indigo-100 disabled:opacity-50 transition-colors"
            >
              {translating ? (
                <><span className="animate-spin inline-block w-3 h-3 border-2 border-indigo-400 border-t-transparent rounded-full" /> Translating...</>
              ) : (
                <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" /></svg> Translate to English</>
              )}
            </button>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 rounded-full">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
              Translated from {detectedLang}
              <button onClick={() => { setTranslated(null); setDetectedLang(null); }} className="ml-1 text-green-600 hover:text-green-800 underline">Undo</button>
            </span>
          )}
        </div>

        {/* Tags */}
        <TagManager entity={entity} token={token} onTagChange={onTagChange} entityType="candidate" />
      </div>

      {/* Contact info */}
      <Section title="Contact Information">
        <ContactInfoGrid
          email={entity.email}
          personalEmail={entity.personal_email}
          phone={entity.phone}
          linkedin={entity.li_profile_url}
          emailSearched={entity.email_searched}
          personalEmailSearched={entity.personal_email_searched}
          phoneSearched={entity.phone_searched}
        />
        <EnrichmentActions
          entityType="candidate"
          entity={entity}
          onEnrich={onEnrich}
          enriching={enriching}
          enrichError={enrichError}
        />
      </Section>

      {/* Shortlist to Job */}
      <ShortlistToJob entityType="candidate" entityId={entity.id} token={token} />

      {/* Summary */}
      {entity.summary && (
        <Section title="Summary">
          <p className="text-sm text-gray-600 whitespace-pre-line">{hl(t('summary'))}</p>
        </Section>
      )}

      {/* Experience — LinkedIn-style timeline */}
      <ExperienceSection
        entity={entity}
        t={t}
        translated={translated}
        highlightTerms={allTerms}
        projectId={projectId}
        entityType="candidate"
        onRefresh={onRefresh}
      />

      {/* Monitoring change history */}
      <ChangeHistorySection candidateId={entity.id} token={token} />

      {/* Education */}
      <EducationSection entity={entity} highlightTerms={allTerms} />

      {/* Skills */}
      {entity.all_skills_text && (
        <Section title="Skills">
          <div className="flex flex-wrap gap-1.5">
            {entity.all_skills_text.split(',').slice(0, 20).map((skill: string, i: number) => (
              <span key={i} className="px-2 py-0.5 text-xs rounded-full bg-blue-50 text-blue-700">
                {highlightText(skill.trim(), allTerms)}
              </span>
            ))}
          </div>
        </Section>
      )}

      {/* Certifications */}
      <CertificationsSection entity={entity} highlightTerms={allTerms} />

      {/* Languages */}
      <LanguagesSection entity={entity} highlightTerms={allTerms} />

      {/* Project context */}
      <Section title="Project">
        <button
          onClick={() => navigate(`/project/${entity.project_id}`)}
          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
          {entity.project_name}
        </button>
      </Section>
    </>
  );
};


// ─── Tag Manager ─────────────────────────────────────────────────────

const TagManager: React.FC<{
  entity: any;
  token: string | null;
  onTagChange?: () => void;
  entityType?: 'candidate' | 'company' | 'manager';
}> = ({ entity, token, onTagChange, entityType = 'candidate' }) => {
  const [tags, setTags] = useState<string[]>(entity.tags || []);
  const [inputValue, setInputValue] = useState('');
  const [showInput, setShowInput] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Sync tags when entity changes
  useEffect(() => {
    setTags(entity.tags || []);
  }, [entity.id, entity.tags]);

  // Fetch project tag suggestions when input opens (entity-type-aware)
  useEffect(() => {
    if (showInput && token && entity.project_id) {
      const tagEndpoint = entityType === 'company'
        ? 'companies'
        : entityType === 'manager'
          ? 'hiring-managers'
          : 'candidates';
      fetch(`/api/${tagEndpoint}/${entity.project_id}/tags`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(r => r.json())
        .then(data => setSuggestions(data.tags || []))
        .catch(() => {});
    }
  }, [showInput, token, entity.project_id, entityType]);

  // Focus input when opened
  useEffect(() => {
    if (showInput) inputRef.current?.focus();
  }, [showInput]);

  const addTag = async (tag: string) => {
    const trimmed = tag.trim();
    if (!trimmed || !token || tags.includes(trimmed)) return;

    setSaving(true);
    try {
      const tagApiBase = entityType === 'company'
        ? `/api/companies/${entity.project_id}/bulk-tag`
        : entityType === 'manager'
          ? `/api/hiring-managers/${entity.project_id}/bulk-tag`
          : `/api/candidates/${entity.project_id}/bulk-tag`;
      const idField = entityType === 'company'
        ? 'companyIds'
        : entityType === 'manager'
          ? 'managerIds'
          : 'candidateIds';
      const resp = await fetch(tagApiBase, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ [idField]: [entity.id], tags: [trimmed] })
      });
      if (resp.ok) {
        setTags(prev => [...prev, trimmed]);
        setInputValue('');
        onTagChange?.();
      }
    } catch (err) {
      console.error('Failed to add tag:', err);
    } finally {
      setSaving(false);
    }
  };

  const removeTag = async (tag: string) => {
    if (!token) return;
    setSaving(true);
    try {
      const removeApiBase = entityType === 'company'
        ? `/api/companies/${entity.project_id}/bulk-remove-tag`
        : entityType === 'manager'
          ? `/api/hiring-managers/${entity.project_id}/bulk-remove-tag`
          : `/api/candidates/${entity.project_id}/bulk-remove-tag`;
      const idField = entityType === 'company'
        ? 'companyIds'
        : entityType === 'manager'
          ? 'managerIds'
          : 'candidateIds';
      const resp = await fetch(removeApiBase, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ [idField]: [entity.id], tags: [tag] })
      });
      if (resp.ok) {
        setTags(prev => prev.filter(t => t !== tag));
        onTagChange?.();
      }
    } catch (err) {
      console.error('Failed to remove tag:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag(inputValue);
    } else if (e.key === 'Escape') {
      setShowInput(false);
      setInputValue('');
    }
  };

  // Filter suggestions to exclude already-applied tags
  const filteredSuggestions = suggestions.filter(s =>
    !tags.includes(s) && s.toLowerCase().includes(inputValue.toLowerCase())
  );

  return (
    <div className="mt-3">
      <div className="flex flex-wrap items-center gap-1.5">
        {tags.map((tag, i) => (
          <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full font-medium">
            {tag}
            <button
              onClick={() => removeTag(tag)}
              disabled={saving}
              className="text-blue-500 hover:text-blue-800 disabled:opacity-50"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </span>
        ))}

        {showInput ? (
          <div className="relative">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={() => {
                // Delay to allow suggestion click
                setTimeout(() => {
                  if (!inputValue.trim()) {
                    setShowInput(false);
                  }
                }, 200);
              }}
              placeholder="Tag name..."
              disabled={saving}
              className="px-2 py-0.5 text-xs border border-gray-300 rounded-full w-32 focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
            />
            {/* Suggestions dropdown */}
            {inputValue && filteredSuggestions.length > 0 && (
              <div className="absolute top-full left-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-32 overflow-y-auto">
                {filteredSuggestions.slice(0, 8).map((s, i) => (
                  <button
                    key={i}
                    onMouseDown={e => { e.preventDefault(); addTag(s); }}
                    className="block w-full text-left px-3 py-1.5 text-xs text-gray-700 hover:bg-blue-50 truncate"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <button
            onClick={() => setShowInput(true)}
            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors border border-dashed border-gray-300 hover:border-blue-400"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add tag
          </button>
        )}
      </div>
    </div>
  );
};


// ─── Experience Section ───────────────────────────────────────────────

function parsePositionHistory(entity: any): any[] | null {
  const raw = entity.position_history;
  if (!raw) return null;
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Array.isArray(parsed) && parsed.length > 0) return parsed;
  } catch {}
  return null;
}

function formatPositionDate(dateObj: any): string | null {
  if (!dateObj?.year) return null;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return dateObj.month ? `${months[dateObj.month - 1]} ${dateObj.year}` : `${dateObj.year}`;
}

function formatPositionDateRange(startEndDate: any): string | null {
  if (!startEndDate?.start) return null;
  const start = formatPositionDate(startEndDate.start);
  const end = startEndDate.end ? formatPositionDate(startEndDate.end) : 'Present';
  return start ? `${start} - ${end}` : null;
}

function calculatePositionTenure(startEndDate: any): number | null {
  if (!startEndDate?.start?.year) return null;
  const end = startEndDate.end || { year: new Date().getFullYear(), month: new Date().getMonth() + 1 };
  const months = (end.year - startEndDate.start.year) * 12 + ((end.month || 1) - (startEndDate.start.month || 1));
  return months / 12;
}

const ExperienceSection: React.FC<{
  entity: any;
  t: (field: string) => string;
  translated?: Record<string, string> | null;
  highlightTerms?: string[];
  projectId?: number;
  entityType?: string;
  onRefresh?: () => void;
}> = ({ entity, t, translated, highlightTerms = [], projectId, entityType, onRefresh }) => {
  const hl = (text: string | null | undefined) => highlightText(text, highlightTerms);
  // Translated position_history lookup by index (falls back to the original).
  const tPos = (idx: number, field: string, raw: string | null | undefined) =>
    (translated && translated[`ph_${idx}_${field}`]) ? translated[`ph_${idx}_${field}`] : raw;
  const [showAll, setShowAll] = useState(false);
  const [settingPrimary, setSettingPrimary] = useState<number | null>(null);
  const fullHistory = parsePositionHistory(entity);
  const INITIAL_SHOW = 5;

  // Count how many positions are currently "Present" (no end date)
  const presentPositionCount = fullHistory
    ? fullHistory.filter((p: any) => !p.startEndDate?.end).length
    : 0;

  // Only show the "Set as primary" option for candidates with multiple concurrent present positions
  const canSetPrimary = entityType === 'candidate' && presentPositionCount > 1 && !!projectId;

  const handleSetPrimary = async (index: number) => {
    if (!projectId) return;
    setSettingPrimary(index);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`/api/candidates/${projectId}/candidate/${entity.id}/set-primary-position`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ positionIndex: index })
      });
      if (resp.ok) {
        onRefresh?.();
      } else {
        const err = await resp.json();
        alert(`Failed to set primary role: ${err.error || 'Unknown error'}`);
      }
    } catch (e: any) {
      alert(`Failed to set primary role: ${e.message}`);
    } finally {
      setSettingPrimary(null);
    }
  };

  // Quick stats bar
  const statsBar = (
    <div className="flex flex-wrap gap-3 mb-4">
      {formatLiveTenure(entity) && (
        <span className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-md font-medium">
          Current: {formatLiveTenure(entity)}
        </span>
      )}
      {(() => {
        if (fullHistory) {
          const tenures = fullHistory.map((p: any) => calculatePositionTenure(p.startEndDate)).filter((v: any) => v != null && v > 0) as number[];
          if (tenures.length > 0) {
            const avg = tenures.reduce((a, b) => a + b, 0) / tenures.length;
            return <span className="px-2 py-1 bg-amber-50 text-amber-700 text-xs rounded-md font-medium">Avg Tenure: {formatTenure(avg)}</span>;
          }
        } else {
          const tenures = [liveTenureYears(entity), entity.previous_position_1_years, entity.previous_position_2_years, entity.previous_position_3_years].filter((v: any) => v != null);
          if (tenures.length > 0) {
            const avg = tenures.reduce((a: number, b: number) => a + b, 0) / tenures.length;
            return <span className="px-2 py-1 bg-amber-50 text-amber-700 text-xs rounded-md font-medium">Avg Tenure: {formatTenure(avg)}</span>;
          }
        }
        return null;
      })()}
      {entity.total_experience_years && (
        <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md">{entity.total_experience_years} yrs total exp</span>
      )}
      {(fullHistory ? fullHistory.length : entity.total_positions) ? (
        <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md">{fullHistory ? fullHistory.length : entity.total_positions} positions</span>
      ) : null}
    </div>
  );

  // Full position_history available — render all positions
  if (fullHistory) {
    const visiblePositions = showAll ? fullHistory : fullHistory.slice(0, INITIAL_SHOW);
    const hasMore = fullHistory.length > INITIAL_SHOW;

    // Career stints: consecutive roles at the SAME company share a coloured
    // connector (a promotion path); a company change breaks the line and
    // starts a new colour — so stints read as visual blocks, not one long
    // undifferentiated timeline.
    const companyKeyOf = (p: any) => String(p?.companyName || '').toLowerCase().trim();
    const STINT_COLOURS = ['#2563eb', '#7c3aed', '#059669', '#d97706', '#dc2626', '#0891b2'];
    const stintIdx: number[] = [];
    visiblePositions.forEach((p: any, i: number) => {
      if (i === 0) { stintIdx.push(0); return; }
      const k = companyKeyOf(p);
      stintIdx.push(k && k === companyKeyOf(visiblePositions[i - 1]) ? stintIdx[i - 1] : stintIdx[i - 1] + 1);
    });

    return (
      <Section title="Experience">
        {statsBar}
        <div className="relative">
          {visiblePositions.map((pos: any, idx: number) => {
            const isCurrent = idx === 0 && !pos.startEndDate?.end;
            const tenure = calculatePositionTenure(pos.startEndDate);
            const dateRange = formatPositionDateRange(pos.startEndDate);
            const colour = STINT_COLOURS[stintIdx[idx] % STINT_COLOURS.length];
            const connectsDown = idx < visiblePositions.length - 1 && stintIdx[idx + 1] === stintIdx[idx];
            return (
              <div key={idx} className="relative pl-10 pb-5">
                {connectsDown && (
                  <div
                    className="absolute left-[14px] w-[3px] rounded-full"
                    style={{ top: 18, bottom: -2, backgroundColor: colour, opacity: 0.35 }}
                  />
                )}
                <div
                  className="absolute left-[10px] top-1.5 w-[11px] h-[11px] rounded-full border-2 border-white"
                  style={{
                    backgroundColor: colour,
                    boxShadow: isCurrent ? `0 0 0 3px ${colour}40` : `0 0 0 2px ${colour}26`,
                  }}
                />
                <div>
                  <p className={`text-sm ${isCurrent ? 'font-semibold text-gray-900' : 'font-medium text-gray-800'}`}>
                    {hl(tPos(idx, 'title', pos.title) || 'Unknown Role')}
                  </p>
                  {pos.companyName && (
                    pos.companyLiUrl || pos.companyLinkedInUrl ? (
                      <a
                        href={pos.companyLiUrl || pos.companyLinkedInUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:text-blue-800 hover:underline inline-flex items-center gap-1"
                      >
                        {hl(pos.companyName)}
                        <svg className="w-3 h-3 flex-shrink-0 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    ) : (
                      <p className="text-sm text-gray-600">{hl(pos.companyName)}</p>
                    )
                  )}
                  <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
                    {dateRange && <span>{dateRange}</span>}
                    {tenure != null && <span>({formatTenure(tenure)})</span>}
                    {pos.contractType && <span className="text-gray-400">· {pos.contractType}</span>}
                  </div>
                  {(pos.companyLocation || pos.location) && (
                    <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                      <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {hl(pos.companyLocation || pos.location)}
                    </p>
                  )}
                  {pos.description && (
                    <p className="mt-1.5 text-xs text-gray-600 leading-relaxed whitespace-pre-line ">{hl(tPos(idx, 'description', pos.description))}</p>
                  )}
                  {/* "Set as primary role" button for concurrent Present positions */}
                  {canSetPrimary && !pos.startEndDate?.end && !isCurrent && (
                    <button
                      onClick={() => handleSetPrimary(idx)}
                      disabled={settingPrimary !== null}
                      className="mt-2 text-xs text-blue-600 hover:text-blue-800 hover:underline font-medium disabled:opacity-50"
                      title="Make this the candidate's primary current role for display and enrichment"
                    >
                      {settingPrimary === idx ? 'Setting...' : '→ Set as primary role'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {hasMore && (
          <button
            onClick={() => setShowAll(!showAll)}
            className="mt-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
          >
            {showAll ? 'Show less' : `Show all ${fullHistory.length} positions`}
          </button>
        )}
      </Section>
    );
  }

  // Fallback — use flattened columns (current + previous 1/2/3)
  return (
    <Section title="Experience">
      {statsBar}
      <div className="relative">
        <div className="absolute left-[15px] top-2 bottom-2 w-px bg-gray-200" />
        {(entity.current_position || entity.current_company) && (
          <div className="relative pl-10 pb-5">
            <div className="absolute left-[10px] top-1.5 w-[11px] h-[11px] rounded-full bg-blue-500 border-2 border-white ring-2 ring-blue-200" />
            <div>
              <p className="text-sm font-semibold text-gray-900">{hl(t('current_position'))}</p>
              <p className="text-sm text-gray-700">{hl(entity.current_company)}</p>
              <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
                {entity.current_position_start_date && <span>{formatDateRange(entity.current_position_start_date)}</span>}
                {formatLiveTenure(entity) && <span>({formatLiveTenure(entity)})</span>}
                {(entity.current_contract_type || entity.employment_type) && (
                  <span className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px]">{entity.current_contract_type || entity.employment_type}</span>
                )}
              </div>
              {(entity.current_position_description || t('current_position_description')) && (
                <p className="mt-2 text-xs text-gray-600 leading-relaxed whitespace-pre-line ">{hl(t('current_position_description') || entity.current_position_description)}</p>
              )}
            </div>
          </div>
        )}
        {[1, 2, 3].map(i => {
          const pos = t(`previous_position_${i}`) || entity[`previous_position_${i}`];
          const comp = entity[`previous_company_${i}`];
          const years = entity[`previous_position_${i}_years`];
          const desc = t(`previous_position_${i}_description`) || entity[`previous_position_${i}_description`];
          if (!pos && !comp) return null;
          return (
            <div key={i} className="relative pl-10 pb-5">
              <div className="absolute left-[10px] top-1.5 w-[11px] h-[11px] rounded-full bg-gray-300 border-2 border-white ring-2 ring-gray-100" />
              <div>
                <p className="text-sm font-medium text-gray-800">{hl(pos)}</p>
                {comp && <p className="text-sm text-gray-600">{hl(comp)}</p>}
                {years != null && formatTenure(years) && (
                  <p className="text-xs text-gray-500 mt-0.5">{formatTenure(years)}</p>
                )}
                {desc && (
                  <p className="mt-1.5 text-xs text-gray-600 leading-relaxed whitespace-pre-line ">{hl(desc)}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
};


// ─── Education Section ────────────────────────────────────────────────

function parseJsonField(entity: any, field: string): any[] | null {
  const raw = entity[field];
  if (!raw) return null;
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Array.isArray(parsed) && parsed.length > 0) return parsed;
  } catch {}
  return null;
}

const EducationSection: React.FC<{ entity: any; highlightTerms?: string[] }> = ({ entity, highlightTerms = [] }) => {
  const hl = (text: string | null | undefined) => highlightText(text, highlightTerms);
  const fullHistory = parseJsonField(entity, 'education_history');

  // Try full JSON history first, then fall back to flattened columns
  if (fullHistory) {
    return (
      <Section title="Education">
        <div className="space-y-3">
          {fullHistory.map((edu: any, idx: number) => {
            const dateRange = edu.startEndDate?.start?.year
              ? `${edu.startEndDate.start.year}${edu.startEndDate.end?.year ? ` - ${edu.startEndDate.end.year}` : ''}`
              : null;
            return (
              <div key={idx} className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  {edu.schoolName && <p className="text-sm font-medium text-gray-900">{hl(edu.schoolName)}</p>}
                  {(edu.degreeName || edu.fieldOfStudy) && (
                    <p className="text-sm text-gray-600">
                      {hl([edu.degreeName, edu.fieldOfStudy].filter(Boolean).join(', '))}
                    </p>
                  )}
                  {dateRange && <p className="text-xs text-gray-500 mt-0.5">{dateRange}</p>}
                  {/* Free-typed education text (grades, thesis, activities) */}
                  {edu.description && String(edu.description).trim() && (
                    <p className="text-xs text-gray-500 mt-0.5 italic">{hl(String(edu.description).trim())}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Section>
    );
  }

  // Fallback to flattened columns
  if (!entity.education_1 && !entity.education_2) return null;
  return (
    <Section title="Education">
      <div className="space-y-3">
        {[1, 2].map(i => {
          const school = entity[`education_${i}`];
          const degree = entity[`degree_${i}`];
          const field = entity[`field_of_study_${i}`];
          const years = entity[`education_${i}_years`];
          if (!school && !degree) return null;
          return (
            <div key={i} className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                {school && <p className="text-sm font-medium text-gray-900">{hl(school)}</p>}
                {(degree || field) && (
                  <p className="text-sm text-gray-600">{hl([degree, field].filter(Boolean).join(', '))}</p>
                )}
                {years && <p className="text-xs text-gray-500 mt-0.5">{years}</p>}
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
};


// ─── Certifications Section ──────────────────────────────────────────

const CertificationsSection: React.FC<{ entity: any; highlightTerms?: string[] }> = ({ entity, highlightTerms = [] }) => {
  const hl = (text: string | null | undefined) => highlightText(text, highlightTerms);
  const fullHistory = parseJsonField(entity, 'certifications');

  if (fullHistory) {
    return (
      <Section title="Certifications">
        <div className="space-y-2">
          {fullHistory.map((cert: any, idx: number) => {
            let dateStr: string | null = null;
            if (cert.issuedDate) {
              if (typeof cert.issuedDate === 'object' && cert.issuedDate.year) {
                const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                dateStr = cert.issuedDate.month
                  ? `${months[cert.issuedDate.month - 1]} ${cert.issuedDate.year}`
                  : `${cert.issuedDate.year}`;
              } else {
                dateStr = String(cert.issuedDate);
              }
            }
            return (
              <div key={idx} className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">{hl(cert.name)}</p>
                  {cert.organizationName && <p className="text-sm text-gray-600">{hl(cert.organizationName)}</p>}
                  {dateStr && <p className="text-xs text-gray-500 mt-0.5">Issued {dateStr}</p>}
                </div>
              </div>
            );
          })}
        </div>
      </Section>
    );
  }

  // Fallback to flattened columns
  if (!entity.certification_1 && !entity.certification_2) return null;
  return (
    <Section title="Certifications">
      <div className="space-y-2">
        {[1, 2].map(i => {
          const name = entity[`certification_${i}`];
          const org = entity[`certification_${i}_org`];
          const date = entity[`certification_${i}_date`];
          if (!name) return null;
          return (
            <div key={i} className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">{hl(name)}</p>
                {org && <p className="text-sm text-gray-600">{hl(org)}</p>}
                {date && <p className="text-xs text-gray-500 mt-0.5">Issued {date}</p>}
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
};


// ─── Languages Section ───────────────────────────────────────────────

// Mercator proficiency enums → friendly label + a rank (higher = more fluent)
// used to sort strongest-first and colour the badge.
const LANGUAGE_PROFICIENCY: Record<string, { label: string; rank: number }> = {
  NATIVE_OR_BILINGUAL:  { label: 'Native', rank: 5 },
  FULL_PROFESSIONAL:    { label: 'Full professional', rank: 4 },
  PROFESSIONAL_WORKING: { label: 'Professional working', rank: 3 },
  LIMITED_WORKING:      { label: 'Limited working', rank: 2 },
  ELEMENTARY:           { label: 'Elementary', rank: 1 },
};

const LanguagesSection: React.FC<{ entity: any; highlightTerms?: string[] }> = ({ entity, highlightTerms = [] }) => {
  // Structured [{name, proficiency}] from enrichment (via languages_detail, or
  // straight from the raw enrichment_json). Falls back to the plain names array.
  let detail: Array<{ name: string; proficiency?: string | null }> | null = null;
  const rawDetail = entity.languages_detail || entity.enrichment_json?.person?.languages;
  if (Array.isArray(rawDetail) && rawDetail.length > 0 && typeof rawDetail[0] === 'object') {
    detail = rawDetail.filter((l: any) => l && l.name);
  }

  if (!detail) {
    const languages = entity.languages;
    if (!languages || (Array.isArray(languages) && languages.length === 0)) return null;
    const langList = (Array.isArray(languages) ? languages : [languages]).filter(Boolean);
    if (langList.length === 0) return null;
    detail = langList.map((name: string) => ({ name, proficiency: null }));
  }

  // Strongest proficiency first, then alphabetical.
  const sorted = detail.slice().sort((a, b) => {
    const ra = LANGUAGE_PROFICIENCY[a.proficiency || '']?.rank || 0;
    const rb = LANGUAGE_PROFICIENCY[b.proficiency || '']?.rank || 0;
    if (rb !== ra) return rb - ra;
    return (a.name || '').localeCompare(b.name || '');
  });

  return (
    <Section title="Languages">
      <div className="flex flex-wrap gap-1.5">
        {sorted.map((lang, i) => {
          const prof = lang.proficiency ? LANGUAGE_PROFICIENCY[lang.proficiency] : null;
          return (
            <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full font-medium bg-emerald-50 text-emerald-700">
              {highlightText(lang.name, highlightTerms)}
              {prof && <span className="text-emerald-500 font-normal">· {prof.label}</span>}
            </span>
          );
        })}
      </div>
    </Section>
  );
};


// ─── Company Detail ───────────────────────────────────────────────────

const CompanyDetail: React.FC<{
  entity: any;
  navigate: any;
  onEntityClick: (type: EntityType, id: number) => void;
  bdCampaignContext?: BdCampaignContext;
  onRefresh?: () => void;
}> = ({ entity, navigate, onEntityClick, bdCampaignContext, onRefresh }) => {
  const { token } = useAuth();
  // Prefer the accurate server count (matches the companies table); the
  // linked_candidates list itself is capped at 100 for display.
  const candidateCount = entity.linked_candidates_count ?? entity.linked_candidates?.length ?? 0;
  const contactCount = entity.linked_hiring_managers?.length ?? 0;

  // Campaign selection state
  const [addingContactIds, setAddingContactIds] = useState<Set<number>>(new Set());
  const [addingCandidateIds, setAddingCandidateIds] = useState<Set<number>>(new Set());

  // Find Contacts state
  const [showFindContacts, setShowFindContacts] = useState(false);
  const [findContactsTitles, setFindContactsTitles] = useState('');

  // Add new hiring manager form state
  const [showAddContact, setShowAddContact] = useState(false);
  const [addHmMode, setAddHmMode] = useState<'manual' | 'linkedin'>('manual');
  const [newHiringManagerUrl, setNewHiringManagerUrl] = useState('');
  const [addHiringManagerEnrich, setAddHiringManagerEnrich] = useState(true);
  const [savingContact, setSavingContact] = useState(false);
  const [manualHm, setManualHm] = useState({ firstName: '', lastName: '', jobTitle: '', email: '', linkedinUrl: '' });

  const handleAddToCampaign = async (contactId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!bdCampaignContext) return;
    setAddingContactIds(prev => new Set(prev).add(contactId));
    try {
      await bdCampaignContext.onAddContactToCampaign(contactId);
    } finally {
      setAddingContactIds(prev => {
        const next = new Set(prev);
        next.delete(contactId);
        return next;
      });
    }
  };

  const handleAddCandidateToCampaign = async (candidateId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!bdCampaignContext) return;
    setAddingCandidateIds(prev => new Set(prev).add(candidateId));
    try {
      await bdCampaignContext.onAddCandidateToCampaign(candidateId);
    } finally {
      setAddingCandidateIds(prev => {
        const next = new Set(prev);
        next.delete(candidateId);
        return next;
      });
    }
  };

  const handleSaveNewContact = async () => {
    if (!token || !newHiringManagerUrl.trim()) return;

    // Validate LinkedIn URL
    if (!newHiringManagerUrl.includes('linkedin.com/in/')) {
      alert('Please enter a valid LinkedIn profile URL (e.g. https://www.linkedin.com/in/john-doe)');
      return;
    }

    setSavingContact(true);
    try {
      const resp = await fetch(`/api/hiring-managers/${entity.project_id}/add-single`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          linkedinUrl: newHiringManagerUrl.trim(),
          enrich: addHiringManagerEnrich,
        }),
      });

      const data = await resp.json();

      if (!resp.ok) {
        if (resp.status === 409) {
          alert('This hiring manager already exists in the project');
        } else if (resp.status === 402) {
          alert('Insufficient credits for enrichment');
        } else {
          throw new Error(data.error || 'Failed to add hiring manager');
        }
        return;
      }

      const manager = data.manager;
      if (manager && bdCampaignContext) {
        bdCampaignContext.onContactCreated(manager);
      }

      setNewHiringManagerUrl('');
      setShowAddContact(false);
      onRefresh?.();

      if (data.enriched) {
        const matchMsg = data.companyMatched ? '\n\nMatched to existing company in project!' : '';
        alert(`Hiring manager added and enriched!${matchMsg}\n\nCredits remaining: ${data.creditsRemaining}`);
      } else {
        alert('Hiring manager added (not enriched)');
      }
    } catch (err: any) {
      alert(err.message || 'Failed to add hiring manager');
    } finally {
      setSavingContact(false);
    }
  };

  const handleSaveManualContact = async () => {
    if (!token) return;
    if (!manualHm.firstName.trim() || !manualHm.lastName.trim() || !manualHm.jobTitle.trim() || !manualHm.linkedinUrl.trim()) {
      alert('Please fill in all required fields (First Name, Surname, Job Title, LinkedIn URL)');
      return;
    }
    if (!manualHm.linkedinUrl.includes('linkedin.com/in/')) {
      alert('Please enter a valid LinkedIn profile URL (must contain linkedin.com/in/)');
      return;
    }

    setSavingContact(true);
    try {
      const resp = await fetch(`/api/hiring-managers/${entity.project_id}/add-manual`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: manualHm.firstName.trim(),
          lastName: manualHm.lastName.trim(),
          jobTitle: manualHm.jobTitle.trim(),
          companyId: entity.id,
          email: manualHm.email.trim() || null,
          linkedinUrl: manualHm.linkedinUrl.trim(),
        }),
      });

      const data = await resp.json();

      if (!resp.ok) {
        if (resp.status === 409) {
          alert('This hiring manager already exists in the project');
        } else {
          throw new Error(data.error || 'Failed to add hiring manager');
        }
        return;
      }

      const manager = data.manager;
      if (manager && bdCampaignContext) {
        bdCampaignContext.onContactCreated(manager);
      }

      setManualHm({ firstName: '', lastName: '', jobTitle: '', email: '', linkedinUrl: '' });
      setShowAddContact(false);
      onRefresh?.();

      const matchMsg = data.companyMatched ? '\n\nLinked to this company.' : '';
      alert(`Hiring manager added successfully!${matchMsg}`);
    } catch (err: any) {
      alert(err.message || 'Failed to add hiring manager');
    } finally {
      setSavingContact(false);
    }
  };

  return (
    <>
      {/* Company header */}
      <div className="px-6 py-5">
        <div className="flex items-start gap-4">
          {entity.logo_url ? (
            <img src={entity.logo_url} alt="" className="w-14 h-14 rounded-lg object-contain bg-gray-100 p-1" />
          ) : (
            <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-white text-lg font-semibold">
              {(entity.company_name?.[0] || '?').toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 truncate">{entity.company_name}</h3>
            {entity.industry && <p className="text-sm text-gray-600">{entity.industry}</p>}
            {entity.office_address && (
              <p className="text-sm text-gray-500 mt-0.5">{entity.office_address}</p>
            )}
          </div>
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-3 gap-3 mt-4">
          <QuickStat label="Employees" value={entity.employee_count ? Number(entity.employee_count).toLocaleString() : '-'} />
          <QuickStat label="Candidates" value={candidateCount} />
          <QuickStat label="Contacts" value={contactCount} />
        </div>
      </div>

      {/* Tags */}
      <TagManager entity={entity} token={token} onTagChange={onRefresh} entityType="company" />

      {/* Company info */}
      <Section title="Company Details">
        <InfoRow label="Industry" value={entity.industry} />
        <InfoRow label="Sub-industry" value={(() => {
          const s = entity.subindustry;
          if (!s) return null;
          if (Array.isArray(s) && s.length > 0) return s.join(', ');
          if (typeof s === 'string' && s.startsWith('{')) return s.replace(/[{}]/g, '').split(',').join(', ');
          return Array.isArray(s) && s.length === 0 ? null : s;
        })()} />
        <InfoRow label="Employees" value={entity.employee_count} />
        <InfoRow label="Founded" value={entity.founded} />
        <InfoRow label="Type" value={entity.type} />
        <InfoRow label="Specialties" value={(() => {
          const s = entity.specialties;
          if (!s) return null;
          if (Array.isArray(s)) return s.join(', ');
          if (typeof s === 'string' && s.startsWith('[')) { try { return JSON.parse(s).join(', '); } catch { return s; } }
          return s;
        })()} />
        <InfoRow label="Website" value={entity.website} link />
        <InfoRow label="Domain" value={entity.company_domain || entity.domain} />
        <InfoRow label="Address" value={entity.office_address} />
      </Section>

      {entity.description && (
        <Section title="Description">
          <p className="text-sm text-gray-600 whitespace-pre-line line-clamp-6">{entity.description}</p>
        </Section>
      )}

      {/* Linked candidates — clickable, with campaign selection when in BD Campaign context */}
      {entity.linked_candidates && entity.linked_candidates.length > 0 && (
        <Section title={`Candidates (${candidateCount})`}>
          {candidateCount > entity.linked_candidates.length && (
            <p className="text-xs text-gray-400 mb-1">Showing first {entity.linked_candidates.length} of {candidateCount}</p>
          )}
          <div className="space-y-1 max-h-60 overflow-y-auto">
            {entity.linked_candidates.map((c: any) => {
              const isCandidateInCampaign = bdCampaignContext?.existingCandidateIds.has(c.id) ?? false;
              const isCandidateAdding = addingCandidateIds.has(c.id);

              return (
                <div
                  key={c.id}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-blue-50 text-sm w-full text-left transition-colors"
                >
                  {/* Selection toggle for BD Campaign context */}
                  {bdCampaignContext && (
                    <button
                      onClick={e => isCandidateInCampaign ? undefined : handleAddCandidateToCampaign(c.id, e)}
                      disabled={isCandidateInCampaign || isCandidateAdding}
                      className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                        isCandidateInCampaign
                          ? 'bg-blue-600 border-blue-600 text-white cursor-default'
                          : isCandidateAdding
                            ? 'border-blue-300 bg-blue-50 cursor-wait'
                            : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50 cursor-pointer'
                      }`}
                      title={isCandidateInCampaign ? 'Already in campaign' : 'Add to campaign'}
                    >
                      {isCandidateInCampaign && (
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                      {isCandidateAdding && (
                        <div className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
                      )}
                    </button>
                  )}

                  {/* Clickable candidate info */}
                  <button
                    onClick={() => onEntityClick('candidate', c.id)}
                    className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                  >
                    {c.photo_url ? (
                      <img src={c.photo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-semibold">
                        {(c.name?.[0] || '?').toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-900 font-medium truncate">{c.name}</p>
                      <p className="text-gray-500 text-xs truncate">{c.current_position}</p>
                    </div>
                    <EnrichmentBadge status={c.enrichment_status} small />
                    {isCandidateInCampaign && (
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-600 flex-shrink-0">
                        In campaign
                      </span>
                    )}
                    <svg className="w-4 h-4 text-gray-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {/* Linked hiring managers — with campaign selection when in BD Campaign context */}
      <Section title={
        <div className="flex items-center justify-between w-full">
          <span>Hiring Managers ({contactCount})</span>
          <div className="flex items-center gap-2">
            {entity.org_id && (
              <button
                onClick={() => setShowFindContacts(!showFindContacts)}
                className="flex items-center gap-1 text-xs font-medium text-[#0077B5] hover:text-[#006097] transition-colors"
                title="Search LinkedIn for people at this company"
              >
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                Find Contacts
              </button>
            )}
            <button
              onClick={() => setShowAddContact(!showAddContact)}
              className="flex items-center gap-1 text-xs font-medium text-purple-600 hover:text-purple-700 transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Hiring Manager
            </button>
          </div>
        </div>
      }>
        {/* Find Contacts inline form */}
        {showFindContacts && entity.org_id && (
          <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
            <input
              type="text"
              value={findContactsTitles}
              onChange={e => setFindContactsTitles(e.target.value)}
              placeholder="e.g. CTO, Engineering Manager"
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none bg-white"
              autoFocus
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  const title = findContactsTitles.trim();
                  if (title.length === 0) {
                    window.open(
                      `https://www.linkedin.com/search/results/people/?origin=COMPANY_PAGE_CANNED_SEARCH&currentCompany=%5B%22${entity.org_id}%22%5D`,
                      '_blank'
                    );
                  } else {
                    const encodedTitle = encodeURIComponent(title);
                    window.open(
                      `https://www.linkedin.com/search/results/people/?origin=FACETED_SEARCH&title=%22${encodedTitle}%22&currentCompany=%5B%22${entity.org_id}%22%5D`,
                      '_blank'
                    );
                  }
                  setShowFindContacts(false);
                  setFindContactsTitles('');
                }
              }}
            />
            <p className="text-xs text-gray-500">Enter a job title to filter by. Leave empty to search all. Press Enter or click Search.</p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setShowFindContacts(false); setFindContactsTitles(''); }}
                className="px-3 py-1 text-xs text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const title = findContactsTitles.trim();
                  if (title.length === 0) {
                    window.open(
                      `https://www.linkedin.com/search/results/people/?origin=COMPANY_PAGE_CANNED_SEARCH&currentCompany=%5B%22${entity.org_id}%22%5D`,
                      '_blank'
                    );
                  } else {
                    const encodedTitle = encodeURIComponent(title);
                    window.open(
                      `https://www.linkedin.com/search/results/people/?origin=FACETED_SEARCH&title=%22${encodedTitle}%22&currentCompany=%5B%22${entity.org_id}%22%5D`,
                      '_blank'
                    );
                  }
                  setShowFindContacts(false);
                  setFindContactsTitles('');
                }}
                className="px-3 py-1 text-xs font-medium bg-[#0077B5] text-white rounded-lg hover:bg-[#006097] transition-colors"
              >
                Search LinkedIn
              </button>
            </div>
          </div>
        )}

        {/* Add new hiring manager form */}
        {showAddContact && (
          <div className="mb-3 p-3 bg-purple-50 border border-purple-200 rounded-lg space-y-2">
            {/* Mode toggle */}
            <div className="flex border border-gray-200 rounded-lg overflow-hidden">
              <button
                onClick={() => setAddHmMode('manual')}
                className={`flex-1 px-3 py-1.5 text-xs font-medium transition-colors ${addHmMode === 'manual' ? 'bg-green-600 text-white' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}
              >
                Manual Entry (Free)
              </button>
              <button
                onClick={() => setAddHmMode('linkedin')}
                className={`flex-1 px-3 py-1.5 text-xs font-medium transition-colors ${addHmMode === 'linkedin' ? 'bg-blue-600 text-white' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}
              >
                LinkedIn Enrich
              </button>
            </div>

            {addHmMode === 'manual' ? (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">First Name *</label>
                    <input
                      type="text"
                      value={manualHm.firstName}
                      onChange={e => setManualHm(prev => ({ ...prev, firstName: e.target.value }))}
                      placeholder="John"
                      className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none bg-white"
                      disabled={savingContact}
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">Surname *</label>
                    <input
                      type="text"
                      value={manualHm.lastName}
                      onChange={e => setManualHm(prev => ({ ...prev, lastName: e.target.value }))}
                      placeholder="Smith"
                      className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none bg-white"
                      disabled={savingContact}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">Job Title *</label>
                  <input
                    type="text"
                    value={manualHm.jobTitle}
                    onChange={e => setManualHm(prev => ({ ...prev, jobTitle: e.target.value }))}
                    placeholder="Head of Talent Acquisition"
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none bg-white"
                    disabled={savingContact}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">LinkedIn URL *</label>
                  <input
                    type="text"
                    value={manualHm.linkedinUrl}
                    onChange={e => setManualHm(prev => ({ ...prev, linkedinUrl: e.target.value }))}
                    placeholder="https://www.linkedin.com/in/john-doe"
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none bg-white"
                    disabled={savingContact}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-0.5">Email</label>
                  <input
                    type="email"
                    value={manualHm.email}
                    onChange={e => setManualHm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="john@company.com"
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none bg-white"
                    disabled={savingContact}
                  />
                </div>
                <p className="text-xs text-gray-400">Will be linked to {entity.company_name || 'this company'} automatically</p>
                <div className="flex justify-end gap-2 pt-1">
                  <button
                    onClick={() => { setShowAddContact(false); setManualHm({ firstName: '', lastName: '', jobTitle: '', email: '', linkedinUrl: '' }); }}
                    className="px-3 py-1 text-xs text-gray-500 hover:text-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveManualContact}
                    disabled={!manualHm.firstName.trim() || !manualHm.lastName.trim() || !manualHm.jobTitle.trim() || !manualHm.linkedinUrl.trim() || savingContact}
                    className="px-3 py-1 text-xs font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                  >
                    {savingContact ? 'Adding...' : 'Add Hiring Manager'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <input
                  type="text"
                  value={newHiringManagerUrl}
                  onChange={e => setNewHiringManagerUrl(e.target.value)}
                  placeholder="https://www.linkedin.com/in/john-doe"
                  className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none bg-white"
                  autoFocus
                />
                <p className="text-xs text-gray-500">Paste a LinkedIn profile URL</p>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="enrichHmCheckbox"
                    checked={addHiringManagerEnrich}
                    onChange={e => setAddHiringManagerEnrich(e.target.checked)}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    disabled={savingContact}
                  />
                  <label htmlFor="enrichHmCheckbox" className="text-xs text-gray-700">
                    Enrich data (uses 1 credit)
                  </label>
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <button
                    onClick={() => { setShowAddContact(false); setNewHiringManagerUrl(''); }}
                    className="px-3 py-1 text-xs text-gray-500 hover:text-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveNewContact}
                    disabled={!newHiringManagerUrl.trim() || savingContact}
                    className="px-3 py-1 text-xs font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                  >
                    {savingContact ? 'Adding...' : 'Add Hiring Manager'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {entity.linked_hiring_managers && entity.linked_hiring_managers.length > 0 ? (
          <div className="space-y-1 max-h-72 overflow-y-auto">
            {entity.linked_hiring_managers.map((h: any) => {
              const isInCampaign = bdCampaignContext?.existingContactIds.has(h.id) ?? false;
              const isAdding = addingContactIds.has(h.id);

              return (
                <div
                  key={h.id}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-orange-50 text-sm w-full text-left transition-colors"
                >
                  {/* Selection toggle for BD Campaign context */}
                  {bdCampaignContext && (
                    <button
                      onClick={e => isInCampaign ? undefined : handleAddToCampaign(h.id, e)}
                      disabled={isInCampaign || isAdding}
                      className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                        isInCampaign
                          ? 'bg-purple-600 border-purple-600 text-white cursor-default'
                          : isAdding
                            ? 'border-purple-300 bg-purple-50 cursor-wait'
                            : 'border-gray-300 hover:border-purple-500 hover:bg-purple-50 cursor-pointer'
                      }`}
                      title={isInCampaign ? 'Already in campaign' : 'Add to campaign'}
                    >
                      {isInCampaign && (
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                      {isAdding && (
                        <div className="w-3 h-3 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                      )}
                    </button>
                  )}

                  {/* Clickable contact info */}
                  <button
                    onClick={() => onEntityClick('contact', h.id)}
                    className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                  >
                    <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 text-xs font-semibold flex-shrink-0">
                      {(h.name?.[0] || '?').toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-900 font-medium truncate">{h.name}</p>
                      <p className="text-gray-500 text-xs truncate">{h.job_title}</p>
                    </div>
                    <EnrichmentBadge status={h.enrichment_status} small />
                    {isInCampaign && (
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-600 flex-shrink-0">
                        In campaign
                      </span>
                    )}
                    <svg className="w-4 h-4 text-gray-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-gray-400 py-2">
            No hiring managers linked to this company. Add one above.
          </p>
        )}
      </Section>

      {/* Project context */}
      <Section title="Project">
        <button
          onClick={() => navigate(`/project/${entity.project_id}`)}
          className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
          {entity.project_name}
        </button>
      </Section>
    </>
  );
};


// ─── Contact (Hiring Manager) Detail ──────────────────────────────────

// Parse a Clay date string ("2024-05" or "2014") into the {year, month} shape
// the candidate panel's position/education renderers expect.
function parseClayYearMonth(s: any): { year: number; month?: number } | undefined {
  if (!s || typeof s !== 'string') return undefined;
  const m = s.match(/^(\d{4})(?:-(\d{1,2}))?/);
  if (!m) return undefined;
  const obj: { year: number; month?: number } = { year: parseInt(m[1], 10) };
  if (m[2]) obj.month = parseInt(m[2], 10);
  return obj;
}

// Transform a stored Clay "Find Contacts" profile blob into the exact shape the
// CANDIDATE panel sections consume, so hiring managers render through the same
// layout. Clay extras (courses, connections, followers, revenue band) are
// intentionally dropped — we mirror the candidate layout, not add to it.
function clayEnrichmentToCandidateShape(ed: any): any | null {
  let data = ed;
  if (typeof data === 'string') { try { data = JSON.parse(data); } catch { return null; } }
  if (!data || typeof data !== 'object') return null;

  // Universe profile (added from Search / Contact Search, or backfilled):
  // already in the candidate shape — career history and education exactly
  // as candidate imports store them, plus skills, photo, languages, certs.
  if (data.source === 'universe') {
    const arr = (v: any) => (Array.isArray(v) && v.length ? v : undefined);
    return {
      position_history: arr(data.position_history),
      education_history: arr(data.education_history),
      certifications: arr(data.certifications),
      languages_detail: arr(data.languages),
      all_skills_text: arr(data.skills) ? data.skills.join(', ') : undefined,
      photo_url: data.photo_url || undefined,
      total_positions: Array.isArray(data.position_history) ? data.position_history.length || undefined : undefined,
      summary: data.summary || undefined,
    };
  }

  const exp = Array.isArray(data.experience) ? data.experience : [];
  const position_history = exp.map((e: any) => {
    const start = parseClayYearMonth(e.start_date);
    const end = e.is_current ? undefined : parseClayYearMonth(e.end_date);
    return {
      title: e.title || null,
      companyName: e.company || null,
      companyLiUrl: e.url || null,           // Clay returns the company LinkedIn as `url`
      startEndDate: (start || end) ? { start, end } : undefined,
      companyLocation: e.locality || null,
      description: e.summary || null,
    };
  });

  const edu = Array.isArray(data.education) ? data.education : [];
  const education_history = edu.map((e: any) => ({
    schoolName: e.school_name || null,
    degreeName: e.degree || null,
    fieldOfStudy: e.field_of_study || null,
    startEndDate: {
      start: e.start_date ? { year: parseInt(e.start_date, 10) } : undefined,
      end: e.end_date ? { year: parseInt(e.end_date, 10) } : undefined,
    },
  }));

  const certs = Array.isArray(data.certifications) ? data.certifications : [];
  const certifications = certs.map((c: any) => ({
    name: c.title || null,
    organizationName: c.company_name || null,
  }));

  return {
    position_history: position_history.length ? position_history : undefined,
    education_history: education_history.length ? education_history : undefined,
    certifications: certifications.length ? certifications : undefined,
    total_positions: position_history.length || undefined,
    summary: data.summary || (data.latest_experience && data.latest_experience.summary) || undefined,
  };
}

const ContactDetail: React.FC<{
  entity: any;
  onEnrich: (type: string) => void;
  enriching: string | null;
  enrichError: string | null;
  navigate: any;
}> = ({ entity, onEnrich, enriching, enrichError, navigate }) => {
  const { token } = useAuth();
  // Fill the candidate-layout gaps from the stored Clay profile (experience
  // timeline, education, certifications) without overwriting anything already
  // populated by other enrichment providers.
  const clayShape = clayEnrichmentToCandidateShape(entity.enrichment_data);
  const richEntity = clayShape ? {
    ...entity,
    position_history: entity.position_history || clayShape.position_history,
    education_history: entity.education_history || clayShape.education_history,
    certifications: entity.certifications || clayShape.certifications,
    total_positions: entity.total_positions || clayShape.total_positions,
    summary: entity.summary || clayShape.summary,
    languages_detail: entity.languages_detail || clayShape.languages_detail,
    all_skills_text: entity.all_skills_text || clayShape.all_skills_text,
    photo_url: entity.photo_url || clayShape.photo_url,
  } : entity;
  const isEnriched = !!clayShape;
  return (
  <>
    {/* Profile header */}
    <div className="px-6 py-5">
      <div className="flex items-start gap-4">
        {richEntity.photo_url ? (
          <img src={richEntity.photo_url} alt="" className="w-14 h-14 rounded-full object-cover border-2 border-gray-200" />
        ) : (
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white text-lg font-semibold">
            {(entity.first_name?.[0] || entity.name?.[0] || '?').toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 truncate">{entity.name}</h3>
          {entity.headline && <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">{entity.headline}</p>}
          {entity.job_title && entity.org && (
            <p className="text-sm text-gray-500 mt-1">
              {entity.job_title} at <span className="font-medium text-gray-700">{entity.org}</span>
            </p>
          )}
          {entity.location && (
            <p className="text-sm text-gray-500 mt-1 flex items-center gap-1">
              <svg className="w-3.5 h-3.5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="truncate">{entity.location}</span>
            </p>
          )}
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <EnrichmentBadge status={entity.enrichment_status} />
        {isEnriched && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-100 text-violet-700 text-xs font-medium">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path d="M11.3 1.046a1 1 0 00-1.6 0L8.2 3.1a1 1 0 01-.5.36l-2.4.7a1 1 0 00-.5 1.6l1.5 1.95a1 1 0 01.2.6l-.1 2.5a1 1 0 001.36 1l2.34-.9a1 1 0 01.62 0l2.34.9a1 1 0 001.36-1l-.1-2.5a1 1 0 01.2-.6l1.5-1.95a1 1 0 00-.5-1.6l-2.4-.7a1 1 0 01-.5-.36l-1.5-2.05z"/></svg>
            Full profile
          </span>
        )}
      </div>
    </div>

    {/* Tags */}
    <TagManager entity={entity} token={token} entityType="manager" />

    {/* Contact info */}
    <Section title="Contact Information">
      <ContactInfoGrid
        email={entity.email}
        phone={entity.phone}
        linkedin={entity.li_profile_url}
        emailSearched={entity.email_searched}
        phoneSearched={entity.phone_searched}
      />
      <EnrichmentActions
        entityType="contact"
        entity={entity}
        onEnrich={onEnrich}
        enriching={enriching}
        enrichError={enrichError}
      />
    </Section>

    {/* Shortlist to Job */}
    <ShortlistToJob entityType="contact" entityId={entity.id} token={token} />

    {/* Role info — only without a full profile (the experience timeline
        below covers it when there is one) */}
    {!isEnriched && (
      <Section title="Role">
        <InfoRow label="Job Title" value={entity.job_title} />
        <InfoRow label="Organisation" value={entity.org} />
        <InfoRow label="Department" value={entity.department} />
        <InfoRow label="Seniority" value={entity.seniority_level} />
        <InfoRow label="Location" value={entity.location} />
      </Section>
    )}

    {/* Linked company */}
    {entity.linked_company && (
      <Section title="Company">
        <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-gray-50">
          {entity.linked_company.logo_url ? (
            <img src={entity.linked_company.logo_url} alt="" className="w-10 h-10 rounded-lg object-contain bg-white p-0.5" />
          ) : (
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center text-purple-600 text-sm font-semibold">
              {(entity.linked_company.company_name?.[0] || '?').toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{entity.linked_company.company_name}</p>
            <p className="text-xs text-gray-500">{[entity.linked_company.industry, entity.linked_company.employee_count && `${entity.linked_company.employee_count} employees`].filter(Boolean).join(' · ')}</p>
          </div>
        </div>
      </Section>
    )}

    {richEntity.summary && (
      <Section title="Summary">
        <p className="text-sm text-gray-600 whitespace-pre-line">{richEntity.summary}</p>
      </Section>
    )}

    {/* Candidate-style profile sections, populated from the Clay enrichment blob.
        Each returns null when its data is absent, so non-enriched managers are
        unaffected. */}
    {isEnriched && (
      <>
        <ExperienceSection entity={richEntity} t={(f: string) => richEntity[f]} entityType="contact" />
        <EducationSection entity={richEntity} />
        {richEntity.all_skills_text && (
          <Section title="Skills">
            <div className="flex flex-wrap gap-1.5">
              {String(richEntity.all_skills_text).split(',').slice(0, 20).map((skill: string, i: number) => (
                <span key={i} className="px-2 py-0.5 text-xs rounded-full bg-blue-50 text-blue-700">{skill.trim()}</span>
              ))}
            </div>
          </Section>
        )}
        <CertificationsSection entity={richEntity} />
        <LanguagesSection entity={richEntity} />
      </>
    )}

    {/* Project context */}
    <Section title="Project">
      <button
        onClick={() => navigate(`/project/${entity.project_id}`)}
        className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
        </svg>
        {entity.project_name}
      </button>
    </Section>
  </>
  );
};


// ─── Shortlist to Job ─────────────────────────────────────────────────

const ShortlistToJob: React.FC<{
  entityType: 'candidate' | 'contact';
  entityId: number;
  token: string | null;
}> = ({ entityType, entityId, token }) => {
  const [open, setOpen] = useState(false);
  const [jobs, setJobs] = useState<{id: number; title: string; company_name: string}[]>([]);
  const [loading, setLoading] = useState(false);
  const [shortlisting, setShortlisting] = useState<number | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const fetchJobs = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const resp = await fetch('/api/campaigns?mode=delivery&status=open', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.ok) {
        const data = await resp.json();
        setJobs(data.map((c: any) => ({ id: c.id, title: c.name, company_name: c.company_name })));
      }
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleShortlist = async (jobId: number) => {
    if (!token) return;
    setShortlisting(jobId);
    try {
      const resp = await fetch(`/api/campaigns/${jobId}/cards/bulk`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ entities: [{ type: entityType, id: entityId }] }),
      });
      if (!resp.ok) throw new Error('Failed');
      const data = await resp.json();
      const jobName = jobs.find(j => j.id === jobId)?.title || 'job';
      setMessage(data.added > 0 ? `Added to "${jobName}" shortlist` : `Already in "${jobName}" shortlist`);
      setOpen(false);
      setTimeout(() => setMessage(null), 4000);
    } catch (err) {
      setMessage('Failed to shortlist');
      setTimeout(() => setMessage(null), 4000);
    } finally {
      setShortlisting(null);
    }
  };

  return (
    <Section title="Shortlist to Job">
      {message ? (
        <p className="text-sm text-amber-700 bg-amber-50 px-3 py-2 rounded-lg">{message}</p>
      ) : !open ? (
        <button
          onClick={() => { setOpen(true); fetchJobs(); }}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-amber-700 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors w-full"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          Add to Job Shortlist
        </button>
      ) : (
        <div className="space-y-1">
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-amber-600" />
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-sm text-gray-400 text-center py-3">
              No open jobs. <button onClick={() => setOpen(false)} className="text-blue-600 hover:underline">Close</button>
            </div>
          ) : (
            <>
              {jobs.map(job => (
                <button
                  key={job.id}
                  onClick={() => handleShortlist(job.id)}
                  disabled={shortlisting === job.id}
                  className="flex items-center gap-2 w-full text-left px-3 py-2 rounded-lg hover:bg-amber-50 transition-colors text-sm disabled:opacity-50"
                >
                  <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-gray-900">{job.title}</span>
                    {job.company_name && <span className="text-gray-400 ml-1">— {job.company_name}</span>}
                  </div>
                  {shortlisting === job.id && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-amber-600" />
                  )}
                </button>
              ))}
              <button onClick={() => setOpen(false)} className="w-full text-center text-xs text-gray-400 hover:text-gray-600 py-1">
                Cancel
              </button>
            </>
          )}
        </div>
      )}
    </Section>
  );
};


// ═══════════════════════════════════════════════════════════════════════
// Shared sub-components
// ═══════════════════════════════════════════════════════════════════════

// Format YYYY-MM date string to readable format (e.g. "Jan 2023 - Present")
function formatDateRange(startDate: string, endDate?: string | null): string {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const fmt = (d: string) => {
    const [y, m] = d.split('-');
    return m ? `${months[parseInt(m) - 1]} ${y}` : y;
  };
  const start = fmt(startDate);
  const end = endDate ? fmt(endDate) : 'Present';
  return `${start} - ${end}`;
}


const Section: React.FC<{ title: React.ReactNode; children: React.ReactNode }> = ({ title, children }) => (
  <div className="px-6 py-4">
    <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">{title}</h4>
    {children}
  </div>
);

const InfoRow: React.FC<{ label: string; value: any; link?: boolean }> = ({ label, value, link }) => {
  if (!value && value !== 0) return null;
  return (
    <div className="flex items-start py-1.5">
      <span className="text-sm text-gray-500 w-36 flex-shrink-0">{label}</span>
      {link ? (
        <a href={String(value).startsWith('http') ? value : `https://${value}`} target="_blank" rel="noopener noreferrer"
          className="text-sm text-blue-600 hover:underline truncate">
          {value}
        </a>
      ) : (
        <span className="text-sm text-gray-900 break-words min-w-0">{String(value)}</span>
      )}
    </div>
  );
};

const QuickStat: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
  <div className="bg-gray-50 rounded-lg p-3 text-center">
    <p className="text-lg font-semibold text-gray-900">{value}</p>
    <p className="text-xs text-gray-500">{label}</p>
  </div>
);

const EnrichmentBadge: React.FC<{ status: string | null; small?: boolean }> = ({ status, small }) => {
  const base = small ? 'text-xs px-1.5 py-0.5' : 'text-xs px-2.5 py-1';
  if (status === 'found') {
    return <span className={`${base} rounded-full bg-green-100 text-green-700 font-medium`}>Enriched</span>;
  }
  if (status === 'in_progress') {
    return <span className={`${base} rounded-full bg-yellow-100 text-yellow-700 font-medium`}>Enriching...</span>;
  }
  if (status === 'not_found') {
    return <span className={`${base} rounded-full bg-gray-100 text-gray-500 font-medium`}>Not found</span>;
  }
  return <span className={`${base} rounded-full bg-gray-100 text-gray-400 font-medium`}>Not enriched</span>;
};

const EntityIcon: React.FC<{ type: EntityType }> = ({ type }) => {
  const classes = "w-8 h-8 rounded-lg flex items-center justify-center";
  if (type === 'candidate') {
    return (
      <div className={`${classes} bg-blue-100`}>
        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      </div>
    );
  }
  if (type === 'company') {
    return (
      <div className={`${classes} bg-purple-100`}>
        <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
      </div>
    );
  }
  return (
    <div className={`${classes} bg-orange-100`}>
      <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    </div>
  );
};

const ContactInfoGrid: React.FC<{
  email?: string;
  personalEmail?: string;
  phone?: string;
  linkedin?: string;
  emailSearched?: boolean;
  personalEmailSearched?: boolean;
  phoneSearched?: boolean;
}> = ({ email, personalEmail, phone, linkedin, emailSearched, personalEmailSearched, phoneSearched }) => (
  <div className="space-y-2">
    <ContactInfoItem icon="email" label="Work Email" value={email} searched={emailSearched} />
    {personalEmail !== undefined && (
      <ContactInfoItem icon="email" label="Personal Email" value={personalEmail} searched={personalEmailSearched} />
    )}
    <ContactInfoItem icon="phone" label="Phone" value={phone} searched={phoneSearched} />
    {linkedin && (
      <div className="flex items-center gap-3 py-1">
        <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
        <a href={linkedin} target="_blank" rel="noopener noreferrer"
          className="text-sm text-blue-600 hover:underline truncate flex-1">
          LinkedIn Profile
        </a>
      </div>
    )}
  </div>
);

const ContactInfoItem: React.FC<{
  icon: 'email' | 'phone';
  label: string;
  value?: string;
  searched?: boolean;
}> = ({ icon, label, value, searched }) => {
  if (!value && !searched) return null;

  return (
    <div className="flex items-center gap-3 py-1">
      {icon === 'email' ? (
        <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ) : (
        <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-400">{label}</p>
        {value ? (
          <>
            <p className="text-sm text-gray-900 max-md:hidden">{value}</p>
            {/* Phones: tap to call / email */}
            <a href={`${icon === 'phone' ? 'tel:' : 'mailto:'}${String(value).replace(icon === 'phone' ? /[^\d+]/g : /\s/g, '')}`}
              className="md:hidden block text-base text-blue-600 underline break-all py-0.5">{value}</a>
          </>
        ) : searched ? (
          <p className="text-sm text-gray-400 italic">Not found</p>
        ) : null}
      </div>
    </div>
  );
};

const EnrichmentActions: React.FC<{
  entityType: 'candidate' | 'contact';
  entity: any;
  onEnrich: (type: string) => void;
  enriching: string | null;
  enrichError: string | null;
}> = ({ entityType, entity, onEnrich, enriching, enrichError }) => {
  const isEnriching = entity.enrichment_status === 'in_progress' || enriching;

  // A field is "done" once it either has a value or was searched and came back
  // empty (we record *_searched for that). Once done, hide the corresponding
  // button — clicking it again would just spend more credits for nothing, and
  // the field area above already shows the value or "Not found".
  const workEmailDone = !!entity.email || !!entity.email_searched;
  const personalEmailDone = entityType !== 'candidate' || !!entity.personal_email || !!entity.personal_email_searched;
  const phoneDone = !!entity.phone || !!entity.phone_searched;
  // Match the original "All" visibility — show if work email OR phone is
  // still pending (the original logic ignored personal_email for this gate).
  const showAllButton = !workEmailDone || !phoneDone;

  if (workEmailDone && personalEmailDone && phoneDone) return null;

  return (
    <div className="mt-3 pt-3 border-t border-gray-100">
      <p className="text-xs font-medium text-gray-500 mb-2">Enrich Data</p>
      <div className="flex flex-wrap gap-2">
        {!workEmailDone && (
          <EnrichButton
            label="Work Email"
            type="work_email"
            loading={enriching === 'work_email'}
            disabled={!!isEnriching}
            onClick={() => onEnrich('work_email')}
          />
        )}
        {entityType === 'candidate' && !personalEmailDone && (
          <EnrichButton
            label="Personal Email"
            type="personal_email"
            loading={enriching === 'personal_email'}
            disabled={!!isEnriching}
            onClick={() => onEnrich('personal_email')}
          />
        )}
        {!phoneDone && (
          <EnrichButton
            label="Phone"
            type="phone"
            loading={enriching === 'phone'}
            disabled={!!isEnriching}
            onClick={() => onEnrich('phone')}
          />
        )}
        {showAllButton && (
          <EnrichButton
            label="All"
            type="all"
            loading={enriching === 'all'}
            disabled={!!isEnriching}
            onClick={() => onEnrich('all')}
          />
        )}
      </div>
      {enrichError && (
        <p className="text-xs text-red-500 mt-2">{enrichError}</p>
      )}
    </div>
  );
};

const EnrichButton: React.FC<{
  label: string;
  type: string;
  loading: boolean;
  disabled: boolean;
  onClick: () => void;
}> = ({ label, loading, disabled, onClick }) => (
  <button
    onClick={onClick}
    disabled={disabled || loading}
    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors
      ${loading
        ? 'bg-gray-100 text-gray-500 cursor-wait'
        : 'bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed'}`}
  >
    {loading ? (
      <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
      </svg>
    ) : (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    )}
    {loading ? `Searching for ${label.toLowerCase()}…` : label}
  </button>
);


export default EntityDetailPanel;
